import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button
} from '@mui/material';
import {
  Download,
  Headphones,
  TrendingUp,
  Block
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { createApiClient } from '../../utils/api';
import { LoadingSpinner } from '@horwath-htl/shared-ui';

export const ReportsAnalytics: React.FC = () => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const api = createApiClient(authenticatedFetch);
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'analytics', startDate, endDate],
    queryFn: () => api.get('/api/marketcompass/admin/reports/analytics', {
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString()
    })
  });

  if (isLoading) return <LoadingSpinner />;

  const analytics = data?.data;

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Date Range Selector */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(date) => setStartDate(date)}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(date) => setEndDate(date)}
            />
            <Button variant="contained" onClick={() => refetch()}>
              Update
            </Button>
          </Box>
        </Paper>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Download sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Total Downloads
                    </Typography>
                    <Typography variant="h4">
                      {analytics?.summary.successful_attempts || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUp sx={{ mr: 2, color: 'success.main' }} />
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Success Rate
                    </Typography>
                    <Typography variant="h4">
                      {analytics?.summary.total_attempts > 0
                        ? Math.round(
                            (analytics.summary.successful_attempts /
                              analytics.summary.total_attempts) *
                              100
                          )
                        : 0}%
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Block sx={{ mr: 2, color: 'error.main' }} />
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Access Denied
                    </Typography>
                    <Typography variant="h4">
                      {analytics?.summary.denied_attempts || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Headphones sx={{ mr: 2, color: 'info.main' }} />
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Podcasts Streamed
                    </Typography>
                    <Typography variant="h4">
                      {analytics?.accessByType?.find((t: any) => t.access_type === 'stream')?.count || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3}>
          {/* Access by Market */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Access by Market
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics?.accessByMarket || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="market_name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="access_count" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Access by Type */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Access by Type
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={analytics?.accessByType || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => `${entry.access_type}: ${entry.count}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {analytics?.accessByType?.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Top Reports */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Top Reports
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Report</TableCell>
                      <TableCell>Market</TableCell>
                      <TableCell align="right">Downloads</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics?.topReports?.map((report: any) => (
                      <TableRow key={report.id}>
                        <TableCell>{report.short_description}</TableCell>
                        <TableCell>{report.market_name}</TableCell>
                        <TableCell align="right">{report.download_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Top Podcasts */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Top Podcasts
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Podcast</TableCell>
                      <TableCell>Market</TableCell>
                      <TableCell align="right">Streams</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics?.topPodcasts?.map((podcast: any) => (
                      <TableRow key={podcast.id}>
                        <TableCell>{podcast.title}</TableCell>
                        <TableCell>{podcast.market_name}</TableCell>
                        <TableCell align="right">{podcast.stream_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};